import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import { toast } from 'react-hot-toast';
import ConfirmModal from '../ConfirmModal';
import './MenuBrowser.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const MEAL_CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'snack', label: 'Snack' }
];

const MenuBrowser = ({ onAddMenuItem, isMenuItemInPlan, isCoachMode, affiliateId, canAddMenuItem, menuSource, onClearPlan }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebounce(searchQuery, 300);
  const [allMenuItems, setAllMenuItems] = useState([]); // All fetched items
  const [loading, setLoading] = useState(false);
  const [cuisines, setCuisines] = useState([]);
  const [dietaryOptions, setDietaryOptions] = useState([]);
  const [selectedCuisine, setSelectedCuisine] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedMealType, setSelectedMealType] = useState('lunch');
  const [selectedDietary, setSelectedDietary] = useState([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Fetch cuisines and dietary options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [cuisineRes, dietaryRes] = await Promise.all([
          fetch(`${API_URL}/menu/cuisines`),
          fetch(`${API_URL}/menu/dietary-options`)
        ]);
        
        if (cuisineRes.ok) {
          const data = await cuisineRes.json();
          const cuisineList = data.cuisines || data || [];
          setCuisines(Array.isArray(cuisineList) ? cuisineList : []);
        }
        
        if (dietaryRes.ok) {
          const data = await dietaryRes.json();
          setDietaryOptions(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Failed to fetch options:', err);
      }
    };
    fetchOptions();
  }, []);

  // Fetch menu items with appropriate filtering for nutrition plan builder
  // - Affiliates creating client plans: only their own menus (for proper attribution)
  // - Regular users: only FreshFitLab/admin menus (excludes other affiliates' menus)
  useEffect(() => {
    const fetchMenuItems = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('limit', '100');
        params.append('forNutritionPlan', 'true');
        
        // If coach mode with affiliate ID, filter to show only their menus
        if (isCoachMode && affiliateId) {
          params.append('creatorAffiliateId', affiliateId);
        }
        // Otherwise, the backend will only return admin/FreshFitLab menus

        const response = await fetch(`${API_URL}/menu?${params}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const items = data.menuItems || data || [];
          setAllMenuItems(Array.isArray(items) ? items : []);
        } else {
          setAllMenuItems([]);
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setAllMenuItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMenuItems();
  }, [isCoachMode, affiliateId]);

  // Client-side filtering (like main menu page)
  const filteredMenuItems = useMemo(() => {
    return allMenuItems.filter(item => {
      // Search filter
      const searchLower = debouncedQuery.toLowerCase();
      const matchesSearch = !debouncedQuery || 
        item.title?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        (item.ingredients && item.ingredients.some(ing => 
          (typeof ing === 'object' && ing.name 
            ? ing.name.toLowerCase().includes(searchLower)
            : String(ing).toLowerCase().includes(searchLower))
        )) ||
        (item.cuisine && item.cuisine.toLowerCase().includes(searchLower));

      // Cuisine filter
      const matchesCuisine = !selectedCuisine || 
        (item.cuisine && item.cuisine.toLowerCase() === selectedCuisine.toLowerCase());

      // Category filter (based on tags)
      const matchesCategory = !selectedCategory ||
        item.tags?.some(tag => tag.toLowerCase() === selectedCategory.toLowerCase());

      // Dietary filter - item must have ALL selected dietary tags
      const matchesDietary = selectedDietary.length === 0 ||
        selectedDietary.every(filterValue =>
          item.dietary?.some(itemTag =>
            (itemTag || '').toLowerCase() === (filterValue || '').toLowerCase()
          )
        );

      return matchesSearch && matchesCuisine && matchesCategory && matchesDietary;
    }).slice(0, 20); // Limit displayed results
  }, [allMenuItems, debouncedQuery, selectedCuisine, selectedCategory, selectedDietary]);

  // Toggle dietary filter
  const handleDietaryToggle = (option) => {
    setSelectedDietary(prev => 
      prev.includes(option)
        ? prev.filter(d => d !== option)
        : [...prev, option]
    );
  };

  // Handle adding menu item -- always allow, warn if mixed sources
  const handleAddMenuItem = (menuItem) => {
    if (canAddMenuItem) {
      const { isMixed, reason } = canAddMenuItem(menuItem);
      if (isMixed && reason) {
        toast(reason, { icon: '\u26A0\uFE0F', duration: 4000 });
      }
    }
    
    onAddMenuItem(menuItem, selectedMealType);
    toast.success(`Added "${menuItem.title}" to ${selectedMealType}`, { duration: 1500 });
  };

  // Parse nutrition value
  const parseNutrition = (value) => {
    if (!value) return '-';
    if (typeof value === 'number') return value;
    return value.replace(/[^0-9.]/g, '') || '-';
  };

  // Determine source label for display
  const getSourceLabel = () => {
    if (!menuSource) return null;
    if (menuSource === 'freshmenulab') return 'FreshFitLab';
    return 'Affiliate';
  };

  return (
    <div className="menu-browser">
      {/* Coach Mode Notice - explains menu filtering for attribution */}
      {isCoachMode && (
        <div className="coach-mode-notice">
          <i className="fas fa-info-circle"></i>
          <div>
            <strong>Building plan for client</strong>
            <p>Menus you have created and stock FreshFitLab recipes are available. When your client shops these recipes, you'll earn commission on their purchases.</p>
          </div>
        </div>
      )}
      
      {/* Menu Source Lock Notice - shows when menus are already in cart */}
      {!isCoachMode && menuSource && (
        <div className="menu-source-notice">
          <i className="fas fa-lock"></i>
          <div>
            <strong>Menu Source: {getSourceLabel()}</strong>
            <p>Your plan uses {getSourceLabel()} menus. To maintain proper order attribution, only {getSourceLabel()} menus can be added.</p>
          </div>
          {onClearPlan && (
            <button 
              className="clear-source-btn"
              onClick={() => setShowClearConfirm(true)}
            >
              <i className="fas fa-sync-alt"></i> Switch Source
            </button>
          )}
        </div>
      )}
      
      {/* Search Controls */}
      <div className="menu-search-controls">
        <div className="search-input-wrapper">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search recipes (e.g., chicken stir fry, pasta...)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-btn" onClick={() => setSearchQuery('')}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        <select
          value={selectedCuisine}
          onChange={(e) => setSelectedCuisine(e.target.value)}
          className="cuisine-select"
        >
          <option value="">All Cuisines</option>
          {cuisines.map(cuisine => (
            <option key={cuisine} value={cuisine}>{cuisine}</option>
          ))}
        </select>
      </div>

      {/* Dietary Filters - user can toggle to filter */}
      {dietaryOptions.length > 0 && (
        <div className="dietary-filter-section">
          <span className="filter-label">
            <i className="fas fa-leaf"></i> Dietary:
          </span>
          <div className="dietary-chips">
            {dietaryOptions.slice(0, 8).map(option => (
              <button
                key={option}
                className={`dietary-chip ${selectedDietary.includes(option) ? 'active' : ''}`}
                onClick={() => handleDietaryToggle(option)}
              >
                {selectedDietary.includes(option) && <i className="fas fa-check"></i>}
                {option}
              </button>
            ))}
            {selectedDietary.length > 0 && (
              <button
                className="dietary-chip clear-chip"
                onClick={() => setSelectedDietary([])}
              >
                <i className="fas fa-times"></i> Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Category Filter - synced with meal type */}
      <div className="category-filter">
        {MEAL_CATEGORIES.map(cat => (
          <button
            key={cat.value}
            className={`category-btn ${selectedCategory === cat.value ? 'active' : ''}`}
            onClick={() => {
              setSelectedCategory(cat.value);
              // Also update the meal type when filtering
              if (cat.value && cat.value !== 'snack') {
                setSelectedMealType(cat.value);
              } else if (cat.value === 'snack') {
                setSelectedMealType('snacks');
              }
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Meal Type Selector */}
      <div className="meal-type-selector">
        <span className="selector-label">Add to:</span>
        <div className="meal-type-buttons">
          {['breakfast', 'lunch', 'dinner', 'snacks'].map(type => (
            <button
              key={type}
              className={`meal-type-btn ${selectedMealType === type ? 'active' : ''}`}
              onClick={() => setSelectedMealType(type)}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Menu Results */}
      <div className="menu-results">
        {loading ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i> Loading recipes...
          </div>
        ) : filteredMenuItems.length > 0 ? (
          <div className="menu-list">
            {filteredMenuItems.map(item => (
              <div key={item._id || item.menuId} className="menu-item">
                {item.image && (
                  <div className="menu-image">
                    <img src={item.image} alt={item.title} />
                  </div>
                )}
                <div className="menu-info">
                  <h4 title={item.title}>{item.title}</h4>
                  <div className="menu-meta">
                    <span className="cuisine">{item.cuisine}</span>
                    {item.totalTime && <span className="time"><i className="fas fa-clock"></i> {item.totalTime}</span>}
                    {item.servings && <span className="servings"><i className="fas fa-utensils"></i> {item.servings} servings</span>}
                  </div>
                  {item.nutrition && (
                    <div className="menu-macros">
                      <span className="macro calories">{parseNutrition(item.nutrition.Calories)} cal</span>
                      <span className="macro protein">{parseNutrition(item.nutrition.Protein)}g P</span>
                      <span className="macro carbs">{parseNutrition(item.nutrition.Carbohydrates)}g C</span>
                      <span className="macro fat">{parseNutrition(item.nutrition.Fat)}g F</span>
                    </div>
                  )}
                </div>
                <button
                  className={`add-btn ${isMenuItemInPlan && isMenuItemInPlan(item._id) ? 'in-plan' : ''}`}
                  onClick={() => handleAddMenuItem(item)}
                >
                  <i className="fas fa-plus"></i>
                  {isMenuItemInPlan && isMenuItemInPlan(item._id) ? 'Add More' : 'Add'}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <i className="fas fa-utensils"></i>
            <p>{allMenuItems.length > 0 ? 'No recipes match your filters' : 'Search for recipes from our menu collection'}</p>
            <p className="hint">{selectedDietary.length > 0 ? 'Try removing some dietary filters' : 'Try "chicken", "pasta", or "salad"'}</p>
          </div>
        )}
      </div>
      
      {/* Clear Plan Confirmation Modal */}
      <ConfirmModal
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={() => {
          onClearPlan();
          setShowClearConfirm(false);
          toast.success('Plan cleared. You can now select menus from any source.');
        }}
        title="Switch Menu Source"
        message="This will clear your entire meal plan. All recipes you've added will be removed. Are you sure you want to continue?"
        confirmText="Clear Plan"
        cancelText="Keep Plan"
        type="warning"
      />
    </div>
  );
};

export default MenuBrowser;
